import React from 'react'
import { t } from '../../../i18n'
import submitNavigationForm from './submitNavigationForm'
import { VersionOriginBadge, VersionStatusBadge } from './badges'
import { VersionRowBase } from './types'

interface VersionsPanelProps<Row extends VersionRowBase> {
  versions: Row[]
  versionHref: (version: Row) => string
  restoreUrl?: (version: Row) => string
  canRestore: boolean
  renderBadges?: (version: Row) => React.ReactNode
  renderMeta?: (version: Row) => React.ReactNode
}

const VersionsPanel = <Row extends VersionRowBase>({
  versions,
  versionHref,
  restoreUrl,
  canRestore,
  renderBadges,
  renderMeta
}: VersionsPanelProps<Row>) => {
  const restoreVersion = (version: Row) => {
    if (!restoreUrl) return
    if (
      !window.confirm(
        t('versioning.restore_confirm', { version: version.versionNumber })
      )
    )
      return

    submitNavigationForm(restoreUrl(version), 'post')
  }

  const viewVersion = (version: Row) => {
    window.location.href = versionHref(version)
  }

  if (
    versions.length <= 1 &&
    versions.every((version) => version.status === 'published')
  )
    return null

  const renderVersionRow = (version: Row) => {
    const clickable = !version.current
    const rowClasses = [
      'version-row',
      'd-flex',
      'justify-content-between',
      'align-items-center',
      clickable ? 'version-row--clickable' : 'version-row--current'
    ].join(' ')

    return (
      <div
        key={version.id}
        className={rowClasses}
        role={clickable ? 'link' : undefined}
        tabIndex={clickable ? 0 : undefined}
        title={clickable ? t('versioning.view_version') : undefined}
        onClick={clickable ? () => viewVersion(version) : undefined}
        onKeyDown={
          clickable
            ? (e) => {
                if (e.key === 'Enter') viewVersion(version)
              }
            : undefined
        }
      >
        <div className="flex-grow-1 me-2" style={{ minWidth: 0 }}>
          <strong className="me-2">v{version.versionNumber}</strong>
          <VersionStatusBadge status={version.status} />
          <span className="ms-1">
            <VersionOriginBadge origin={version.origin} />
          </span>
          {renderBadges?.(version)}
          <div style={{ fontSize: '12px', color: '#6F6F6F' }}>
            {version.createdAt}
            {version.createdFromVersionNumber !== null && (
              <span className="ms-2">
                {t('versioning.created_from_version', {
                  version: version.createdFromVersionNumber
                })}
              </span>
            )}
            {renderMeta?.(version)}
          </div>
        </div>
        {canRestore && restoreUrl && version.status === 'archived' ? (
          <button
            type="button"
            className="btn btn-secondary btn-sm inverted"
            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            onClick={(e) => {
              e.stopPropagation()
              restoreVersion(version)
            }}
          >
            <i className="fa fa-undo me-1" />
            {t('versioning.restore_version')}
          </button>
        ) : (
          clickable && (
            <span style={{ color: '#b0b0b0', fontSize: '18px', flexShrink: 0 }}>
              ›
            </span>
          )
        )}
      </div>
    )
  }

  return (
    <div className="mb-3">
      <h5 className="mb-2">{t('versioning.versions')}</h5>
      <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
        {versions.map(renderVersionRow)}
      </div>
    </div>
  )
}

export default VersionsPanel
